// 设置合并行列的key值，方法存取。统一取值方式
export function setMergedColRowSpanKey(rowIndex, columnIndex) {
  return rowIndex + '_' + columnIndex
}

/**
 *
 * @description 创建用于计算合并行列的方法队列
 * @export
 * @param {*} data
 * @param {*} { calculateSpanMethods, getEqualCellsMap }
 * @return {*}
 */
export function createMergeCellsTaskQueue(
  data,
  { calculateSpanMethods, getEqualCellsMap }
) {
  const taskQueue = [
    (result, index) => calculateSpanMethods(result, index, data),
    (result, index) => getEqualCellsMap(result, index, data),
  ]
  return taskQueue
}

/**
 *
 * @description 创建合并行列需要的数据，在table数据更新变化时调用
 * @export
 * @param {*} formatDataResult
 * @param {*} coreInstance
 * @return {*}
 */
export function mergeCellsData(formatDataResult, coreInstance) {
  coreInstance.store.clearEqualCellsMap()
  coreInstance.store.clearMergeRowColspanMap()
  const {
    getEqualCellsMap,
    calculateSpanMethods,
    usingRowColMerge,
  } = coreInstance.store
  const taskQueue = createMergeCellsTaskQueue(formatDataResult.tableData, {
    getEqualCellsMap,
    calculateSpanMethods,
  })
  if (!usingRowColMerge) {
    return formatDataResult
  }
  formatDataResult.tableData.forEach((cur, index) => {
    taskQueue.forEach((taskCallback) => taskCallback(cur, index))
  })
  coreInstance.store.calculateEqualMergeCells()
  return formatDataResult
}

/**
 *
 * @desc 计算单元格数据的相同的合并行列配置
 * @param {*} data
 * @param {*} option
 */
export function calculateEqualMergeCells(data, option) {
  const { direction = 'row', beforeMerge = null } = option
  const mergeCells = []

  for (const cellValue in data) {
    const rowColMap = data[cellValue]
    if (beforeMerge && typeof beforeMerge === 'function') {
      const isMerge = beforeMerge({
        equalCellsMap: data,
        value: cellValue,
        ...rowColMap,
      })
      // 如果返回false就不合并这一列
      if (!isMerge) {
        continue
      }
    }
    if (direction === 'row') {
      mergeCells.push(
        ...createCellOptionFromCellValue(rowColMap.col, 'row', cellValue)
      )
    } else if (direction === 'col') {
      mergeCells.push(
        ...createCellOptionFromCellValue(rowColMap.row, 'col', cellValue)
      )
    }
  }
  return mergeCells
}

/**
 *
 * @description 以单元格数据的维度生成连续且递增的合并行列配置项
 * @param {*} obj
 * @param {*} type
 * @param {*} cellValue
 * @return {*}
 */
function createCellOptionFromCellValue(obj, type, cellValue) {
  const cellValueMergeCells = []

  for (const index in obj) {
    const sequences = findSequentialIncreasingSequences(obj[index])

    sequences.forEach((sequence) => {
      const [end] = sequence
      cellValueMergeCells.push(
        createMergeCellItem(+index, end, type, cellValue, sequence.length)
      )
    })
  }
  return cellValueMergeCells
}

/**
 *
 * @description 计算合并列或者行的项
 * @param {*} start
 * @param {*} end
 * @param {*} type
 * @param {*} cellValue
 * @param {*} len
 * @return {*}
 */
function createMergeCellItem(start, end, type, cellValue, len) {
  if (type === 'row') {
    return { row: end, col: start, rowspan: len, colspan: 1, cellValue }
  } else if (type === 'col') {
    return { row: start, col: end, rowspan: 1, colspan: len, cellValue }
  }
}

/**
 *
 * @desc 寻找连续递增的序列
 * @param {*} arr
 * @return {*}
 */
function findSequentialIncreasingSequences(arr) {
  const sequences = []
  let sequence = []

  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === arr[i - 1] + 1 || i === 0) {
      sequence.push(arr[i])
    } else {
      if (sequence.length > 1) {
        sequences.push([...sequence])
      }
      sequence = [arr[i]]
    }
  }

  if (sequence.length > 1) {
    sequences.push([...sequence])
  }

  return sequences
}

/**
 *
 * @description 构建合并行列需要的键值对
 * @param {*} index
 * @param {*} mergeSum
 * @return {*}
 */
function createMergeItem(index, mergeSum) {
  const mergeIndex = Array.from({ length: mergeSum }, (_, i) => index + i)
  const key = mergeIndex.join(',')
  return { 
    key, 
    value: { 
      mergeSum, 
      mergeIndex,  
      mergeStartIndex: mergeIndex[0],
      mergeEndIndex: mergeIndex[mergeIndex.length - 1],
    } 
  }
}

/**
 *
 * @description 转换mergeCells为合并行列需要的数据
 * @param {*} [mergeCells=[]]
 * @return {*}
 */
export function transformMergeCellsToRowColSpanResult(mergeCells = []) {
  const mergeRowspanMaps = {}
  const mergeColspanMaps = {}
  const mergedColRowSpanResult = {}

  for (const { row, col, rowspan, colspan } of mergeCells) {
    const rowColKey = setMergedColRowSpanKey(row, col)
    mergedColRowSpanResult[rowColKey] = { rowspan, colspan }

    if (rowspan > 1) {
      const { key, value } = createMergeItem(row, rowspan)
      mergeRowspanMaps[key] = value
      for (let diff = 1; diff < rowspan; diff++) {
        const rowColKey = setMergedColRowSpanKey(row + diff, col)
        mergedColRowSpanResult[rowColKey] = { rowspan: 0, colspan: 0 }
      }
    }

    if (colspan > 1) {
      const { key, value } = createMergeItem(col, colspan)
      mergeColspanMaps[key] = value
      for (let diff = 1; diff < colspan; diff++) {
        const rowColKey = setMergedColRowSpanKey(row, col + diff)
        mergedColRowSpanResult[rowColKey] = { rowspan: 0, colspan: 0 }
      }
    }
  }

  return {
    mergedColRowSpanResult,
    mergeRowspanMaps,
    mergeColspanMaps,
  }
}



// WEBPACK FOOTER //
// ./src/public/components/kye-table2/store/config/merge-cell.js